import { useState } from 'react';
import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { useDevToolsStore } from '@frontend/devtools';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Text, RadioField, styles, NakedButton, CaretDownIconSmall, NakedUl } from '@frontend/design-system';
import { ExtendedLocationScope } from '../../views/settings/types';

export type TenantLocationOption = {
  type: 'group' | 'individual';
  locationType: 'databeta' | 'unify' | 'single' | null;
  id: string;
  locationId: string;
  name: string;
  children: TenantLocationOption[];
  phoneTenantId: string;
  orgId: string;
};

export const TenantItem = ({ option }: { option: TenantLocationOption }) => {
  if (option.children.length > 0) {
    return <AccordionItem option={option} />;
  }

  return <BasicItem option={option} />;
};

const DebugLabel = ({ location }: { location: Pick<ExtendedLocationScope, 'id' | 'name'> }) => {
  return (
    <div css={styles.truncate}>
      <Text as='span' css={styles.truncate}>
        {location.name}
      </Text>
      <Text size='medium' color='subdued' css={styles.truncate}>
        {location.id}
      </Text>
    </div>
  );
};

const BasicItem = ({ option }: { option: TenantLocationOption }) => {
  return (
    <RadioField.Radio
      css={[
        {
          label: styles.truncate,
        },
      ]}
      value={option.id}
    >
      <Text as='span'>{option.name}</Text>
    </RadioField.Radio>
  );
};

const AccordionItem = ({ option }: { option: TenantLocationOption }) => {
  const {
    options: { showTenantType: debug },
  } = useDevToolsStore();

  const { t } = useTranslation('phone');

  const [isActive, setIsActive] = useState(false);

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        marginBottom: theme.spacing(2),
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          ...(debug && {
            background: theme.colors.warning50,
          }),
        }}
      >
        <RadioField.Radio value={option.id} css={{ margin: 0, minWidth: 0, label: { minWidth: 0 } }}>
          <div
            style={{
              display: 'flex',
              overflow: 'hidden',
              justifyContent: 'space-between',
              gap: theme.spacing(2),
              width: '316px',
            }}
          >
            {debug ? (
              <DebugLabel location={{ id: option.id, name: option.name }} />
            ) : (
              <Text css={styles.truncate}>{option.name}</Text>
            )}
            <NakedButton
              style={{ display: 'flex', gap: theme.spacing(1), alignItems: 'center' }}
              onClick={() => setIsActive((prev) => !prev)}
            >
              <Text size='medium' color='subdued'>
                {t('{{count}} locations', { count: option.children.length })}
              </Text>
              <div
                style={{
                  transition: 'transform 300ms ease-out',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transform: `rotate(${isActive ? 180 : 0}deg)`,
                }}
              >
                <CaretDownIconSmall color='subdued' />
              </div>
            </NakedButton>
          </div>
        </RadioField.Radio>
      </div>
      <AnimatePresence>
        {isActive && (
          <motion.div
            initial='collapsed'
            animate='open'
            exit='collapsed'
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            variants={{
              open: {
                height: 'auto',
                opacity: 1,
                transition: {
                  height: {
                    duration: 0.2,
                  },
                  opacity: {
                    duration: 0.25,
                  },
                },
              },
              collapsed: {
                height: 0,
                opacity: 0,
                transition: {
                  height: {
                    duration: 0.2,
                  },
                  opacity: {
                    duration: 0.15,
                  },
                },
              },
            }}
          >
            <NakedUl
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(1.5),
                marginTop: theme.spacing(1.5),
                marginInlineStart: theme.spacing(5.5),
              }}
            >
              {option.children.map((childOption) => (
                <li key={childOption.id} css={styles.truncate}>
                  {childOption.name}
                </li>
              ))}
            </NakedUl>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const AppGlobalLocationPickerAlert = ({ openModal }: { openModal: () => void }) => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: ${theme.spacing(2)};
        border: 1px solid ${theme.colors.primary50};
        background: ${theme.colors.primary5};
        padding: ${theme.spacing(2)};
        border-radius: ${theme.borderRadius.small};
      `}
    >
      <div
        css={css`
          min-width: ${theme.fontSize(24)};
        `}
      >
        <Icon
          name='alert-invert'
          color='primary'
          css={css`
            width: 100%;
          `}
          size={24}
        />
      </div>
      <Text
        css={css`
          margin-right: ${theme.spacing(1)};
        `}
        size='medium'
      >
        Not finding what you&apos;re looking for? Adjust your selection in the{' '}
        <Text
          as='span'
          css={css`
            cursor: pointer;
            font-size: inherit;
          `}
          color='primary'
          onClick={openModal}
        >
          Global Location Filter
        </Text>{' '}
        then try again.
      </Text>
    </div>
  );
};
